import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import http from "app/requests/axiosInstance";

export interface ProductCategory {
    id: number;
    name: string;
    accountId: number;
    shopId?: number | null;
    createdAt: string;
    updatedAt: string;
    archivedAt?: string | null;
}

export interface Product {
    id: number;
    title: string;
    description: string;
    duration: number;
    priceCents: number;
    accountId: number;
    createdAt: string;
    updatedAt: string;
    archivedAt?: string | null;
    productCategoryId?: number | null;
    productCategory?: ProductCategory | null;
}

export interface ProductsSliceState {
    publicProducts: Product[];
    products: Product[];
    categories: ProductCategory[];
    loading: boolean;
    error: any;
}

const initialState: ProductsSliceState = {
    publicProducts: [],
    products: [],
    categories: [],
    loading: false,
    error: null,
};

/* ---------------------------------------------------------
 * GET PUBLIC PRODUCTS
 * --------------------------------------------------------- */
// export const getPublicProducts = createAsyncThunk(
//     "products/getPublicProducts",
//     async (_, thunkAPI) => {
//         try {
//             const { data } = await http.get("/v1/products/public");
//             return  data; // support both API styles
//         } catch (error) {
//             return thunkAPI.rejectWithValue(error);
//         }
//     }
// );

/* ---------------------------------------------------------
 * GET AUTHENTICATED PRODUCTS
 * --------------------------------------------------------- */
export const getProducts = createAsyncThunk(
    "products/getProducts",
    async (_, thunkAPI) => {
        try {
            const { data } = await http.get("/v1/products");
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

/* ---------------------------------------------------------
 * CREATE PRODUCT CATEGORY
 * --------------------------------------------------------- */
export const createProductCategory = createAsyncThunk(
    "products/createProductCategory",
    async (payload: { name: string}, thunkAPI) => {
        try {
            const { data } = await http.post("/v1/product-category", payload);
            await thunkAPI.dispatch(getProducts()).unwrap()
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

/* ---------------------------------------------------------
 * UPDATE PRODUCT CATEGORY
 * --------------------------------------------------------- */
export const updateProductCategory = createAsyncThunk(
    "products/updateProductCategory",
    async (
        {
            categoryId,
            data: updateData,
        }: { categoryId: number; data: Partial<ProductCategory> },
        thunkAPI
    ) => {
        try {
            const { data } = await http.patch(`/v1/products/category/${categoryId}`, updateData);
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

/* ---------------------------------------------------------
 * CREATE PRODUCT
 * --------------------------------------------------------- */
export const createProduct = createAsyncThunk(
    "products/createProduct",
    async (payload: any, thunkAPI) => {
        try {
            const { data } = await http.post("/v1/products", payload);
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);
export const deleteProduct = createAsyncThunk(
    "products/deleteProduct",
    async (payload: any, thunkAPI) => {

        try {
            const { data } = await http.delete(`/v1/products/${payload.id}`);
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);
export const deleteProductCategory = createAsyncThunk(
    "products/deleteProductCategory",
    async (payload: any, thunkAPI) => {

        try {
            const { data } = await http.delete(`/v1/product-category/${payload.id}`);
            thunkAPI.dispatch(getProducts())
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

/* ---------------------------------------------------------
 * UPDATE PRODUCT
 * --------------------------------------------------------- */
export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async (
        {
            productId,
            data: updateData,
        }: { productId: number; data: any },
        thunkAPI
    ) => {
        try {
            const { data } = await http.patch(`/v1/products/${productId}`, updateData);
            return  data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

/* ---------------------------------------------------------
 * FUTURE PLACEHOLDERS (delete category, delete product)
 * --------------------------------------------------------- */
// export const deleteProductCategory = createAsyncThunk(...)
// export const deleteProduct = createAsyncThunk(...)

/* ---------------------------------------------------------
 * SLICE
 * --------------------------------------------------------- */
export const productsSlice = createSlice({
    name: "products",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        /* ----------------------------- GET PUBLIC PRODUCTS ----------------------------- */
        // builder.addCase(getPublicProducts.fulfilled, (state, action) => {
        //     state.publicProducts = action.payload;
        // });

        /* ----------------------------- GET USER PRODUCTS ------------------------------ */
        builder.addCase(getProducts.fulfilled, (state, action) => {
            const categories = action.payload;
            state.categories =categories;
            const products = categories.flatMap((cat:any) =>
                (cat.products || []).map((product:any) => ({
                    ...product,
                    productCategory: cat
                }))
            );


            state.products = products;



        });

        /* ----------------------------- CREATE CATEGORY ----------------------------- */
        // builder.addCase(
        //     createProductCategory.fulfilled,
        //     (state, action: PayloadAction<ProductCategory>) => {
        //         state.categories.push(action.payload);
        //     }
        // );

        /* ----------------------------- UPDATE CATEGORY ----------------------------- */
        builder.addCase(
            updateProductCategory.fulfilled,
            (state, action: PayloadAction<ProductCategory>) => {
                const index = state.categories.findIndex((c) => c.id === action.payload.id);
                if (index > -1) state.categories[index] = action.payload;
            }
        );

        /* ----------------------------- CREATE PRODUCT ----------------------------- */
        builder.addCase(
            createProduct.fulfilled,
            (state, action: PayloadAction<Product>) => {
                state.products.push(action.payload);
            }
        );
        /* ----------------------------- DELETE PRODUCT ----------------------------- */
        builder.addCase(
            deleteProduct.fulfilled,
            (state, action: PayloadAction<Product>) => {

               const remainingProducts=state.products.filter((prd)=>prd.id!==action.payload.id)
                state.products=(remainingProducts);
            }
        );
        /* ----------------------------- DELETE PRODUCT CATEGORY ----------------------------- */
        // builder.addCase(
        //     deleteProductCategory.fulfilled,
        //     (state, action: PayloadAction<Product>) => {
        //
        //        const remainingProducts=state.products.filter((prd)=>prd.id!==action.payload.id)
        //         state.products=(remainingProducts);
        //     }
        // );

        /* ----------------------------- UPDATE PRODUCT ----------------------------- */
        builder.addCase(
            updateProduct.fulfilled,
            (state, action: PayloadAction<Product>) => {
                const index = state.products.findIndex((p) => p.id === action.payload.id);
                if (index > -1) state.products[index] = action.payload;
            }
        );

        // /* ----------------------------- ERROR HANDLING ----------------------------- */
        // builder.addMatcher(
        //     (action) => action.type.startsWith("products/") && action.type.endsWith("rejected"),
        //     (state, action) => {
        //         state.error = action.payload ?? action.error;
        //     }
        // );
    },
});

/* ---------------------------------------------------------
 * SELECTORS
 * --------------------------------------------------------- */
export const selectProducts = (state: RootState) => state.products;


export default productsSlice.reducer;
